// PLATFORMA: Local (MINGW64)
// FAILAS: src/pages/MyBotsPage.tsx

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusCircle } from 'lucide-react';
import { useBots } from '@/contexts/BotsContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import BotCard from '../components/BotCard';
import { Button } from '../ui/Button';

const MyBotsPage: React.FC = () => {
  const { bots, practiceBots, editBot, clearCurrentBot, deleteBot, isLoading, fetchBots } = useBots();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [highlightedBotId, setHighlightedBotId] = useState<string | null>(null);

  // Removed localStorage deleted bot IDs tracking - using hard delete now (DB removes records)
  
  // Single fetchBots call on mount - use from context
  useEffect(() => {
    fetchBots();
  }, [fetchBots]);
  
  // Handle focus query parameter for highlighting newly created bots
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (focusId) {
      setHighlightedBotId(focusId);
      
      // Remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedBotId(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams]);
  
  // If bots are still loading, display a loading indicator
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 pt-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading bots...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            If this takes too long, please check your internet connection
          </p>
        </div>
      </div>
    );
  }

  const handleCreateNewBot = () => {
    clearCurrentBot();
    console.log('[MY BOTS] Navigating to create-bot page');
    navigate('/create-bot');
  };

  const handleEditBot = async (botId: string) => {
    await editBot(botId);
    navigate('/create-bot');
  };

  const handleDeleteBot = async (botId: string, isPractice = false) => {
    console.log('[MY BOTS PAGE] Deleting bot:', botId);
    try {
      // Hard delete removes from DB and context refetches
      await deleteBot(botId);
      console.log('[MY BOTS PAGE] Bot deleted successfully');
      // Force refresh to ensure UI consistency
      await fetchBots();
    } catch (error) {
      console.error('[MY BOTS PAGE] Delete error:', error);
      // Error is already shown via toast in BotsContext
    }
  };

  // Show all bots from context (DB hard deletes, so no local filtering needed)
  const visibleBots = bots ?? [];
  const visiblePracticeBots = practiceBots ?? [];
  const customBots = visibleBots.filter(bot => !bot.is_practice && !bot.is_from_marketplace);
  const marketplaceBots = visibleBots.filter(bot => !bot.is_practice && bot.is_from_marketplace && !bot.is_premium);
  const premiumBots = visibleBots.filter(bot => !bot.is_practice && bot.is_from_marketplace && bot.is_premium);

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-12"
      >
              {/* Header with Create Bot Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Bots</h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Manage all your trading bots. Create new bots or activate them from successful backtests.
                  </p>
                </div>
                <Button 
                  variant="primary" 
                  icon={<PlusCircle size={16} />}
                  iconPosition="left"
                  onClick={handleCreateNewBot}
                >
                  Create New Bot
                </Button>
              </div>

              {/* Custom Bots */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Custom Bots</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Bots you've created manually or activated from backtest results
                    </p>
                  </div>
                </div>

                {customBots.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {customBots.map((bot, index) => (
                      <motion.div
                        key={bot.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        data-bot-id={bot.id}
                        className={highlightedBotId === bot.id ? 'ring-4 ring-emerald-500 ring-opacity-50 rounded-xl animate-pulse' : ''}
                      >
                        <BotCard
                          bot={bot}
                          onClick={() => handleEditBot(bot.id)}
                          onDelete={() => handleDeleteBot(bot.id, false)}
                          index={index}
                          variant="my-bots"
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      No custom bots yet. Create your first bot or activate one from successful backtests!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        variant="primary" 
                        onClick={handleCreateNewBot}
                        icon={<PlusCircle size={16} />}
                        iconPosition="left"
                      >
                        Create New Bot
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/backtests')}
                      >
                        View Backtests
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Practice Bots */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Practice Bots</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Test your strategies risk-free with paper trading</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    icon={<PlusCircle size={16} />}
                    iconPosition="left"
                    onClick={handleCreateNewBot}
                  >
                    New Practice Bot
                  </Button>
                </div>

                {visiblePracticeBots.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {visiblePracticeBots.map((bot, index) => (
                      <motion.div
                        key={bot.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className={highlightedBotId === bot.id ? 'ring-4 ring-emerald-500 ring-opacity-50 rounded-xl animate-pulse' : ''}
                      >
                        <BotCard
                          bot={bot}
                          onClick={() => handleEditBot(bot.id)}
                          onDelete={() => handleDeleteBot(bot.id, true)}
                          index={customBots.length + index}
                          variant="my-bots"
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No practice bots yet. Create a practice bot to test strategies!</p>
                  </div>
                )}
              </div>

              {/* Marketplace Bots */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Marketplace Bots</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Bots from our marketplace</p>
                  </div>
                </div>

                {marketplaceBots.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {marketplaceBots.map((bot, index) => (
                      <motion.div
                        key={bot.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className={highlightedBotId === bot.id ? 'ring-4 ring-emerald-500 ring-opacity-50 rounded-xl animate-pulse' : ''}
                      >
                        <BotCard
                          bot={bot}
                          onClick={() => handleEditBot(bot.id)}
                          onDelete={() => handleDeleteBot(bot.id, false)}
                          index={customBots.length + visiblePracticeBots.length + index}
                          variant="my-bots"
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No marketplace bots. Visit the marketplace to discover bots!</p>
                  </div>
                )}
              </div>

              {/* Premium Bots */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Premium Bots</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Premium bots from our marketplace</p>
                  </div>
                </div>

                {premiumBots.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {premiumBots.map((bot, index) => (
                      <motion.div
                        key={bot.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className={highlightedBotId === bot.id ? 'ring-4 ring-emerald-500 ring-opacity-50 rounded-xl animate-pulse' : ''}
                      >
                        <BotCard
                          bot={bot}
                          onClick={() => handleEditBot(bot.id)}
                          onDelete={() => handleDeleteBot(bot.id, false)}
                          index={customBots.length + (practiceBots?.length ?? 0) + marketplaceBots.length + index}
                          variant="my-bots"
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No premium bots. Upgrade your plan to access premium bots!</p>
                  </div>
                )}
              </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default MyBotsPage;